import type { ComponentProps, ElementType } from 'react'
import { z } from 'zod'

export interface WithAsProps<As extends ElementType> {
	as?: As
}

export type ComponentPropsWithAs<Props, As extends ElementType> = Props &
	WithAsProps<As> &
	Omit<Props extends unknown ? ComponentProps<As> : Props, keyof Props>

export enum Format {
	GIF = 'gif',
	Heic = 'heic',
	JPEG = 'jpeg',
	PNG = 'png',
}

export interface LivePhoto {
	imageUrl: string
	videoUrl: string
}

export enum PictureURLKey {
	picUrl = 'picUrl',
	thumbnailUrl = 'thumbnailUrl',
	middlePicUrl = 'middlePicUrl',
	smallPicUrl = 'smallPicUrl',
	watermarkPicUrl = 'watermarkPicUrl',
}
export interface Picture {
	key: string
	thumbnailUrl?: string
	smallPicUrl?: string
	picUrl: string
	format?: Format
	middlePicUrl?: string
	cropperPosX?: number
	cropperPosY?: number
	width?: number
	height?: number
	watermarkPicUrl?: string
	livePhoto?: LivePhoto
}

export interface PictureTheme {
	dark?: Picture
}

export interface ThemesPicture extends Picture {
	themes?: PictureTheme
}

export enum ContentType {
	POST = 'ORIGINAL_POST',
	REPOST = 'REPOST',
	PERSONAL_UPDATE = 'PERSONAL_UPDATE',
	COMMENT = 'COMMENT',
	STORY = 'STORY',
}

const ErrorResponseBodyAlertSchema = z.object({
	title: z.string(),
	description: z.string(),
	confirmButton: z.object({
		text: z.string(),
		linkUrl: z.string().optional(),
	}),
})

const ErrorResponseBodySchema = z.object({
	code: z.union([z.number(), z.string()]),
	message: z.string().optional(),
	error: z.string().optional(),
	toast: z.string().optional(),
	alert: ErrorResponseBodyAlertSchema.optional(),
})
export type ErrorResponseBody = z.infer<typeof ErrorResponseBodySchema>

export interface UrlsInText {
	title: string
	originalUrl: string
	url: string
	type: UrlsInTextType
}

export enum UrlsInTextType {
	LINK = 'link',
	MENTION = 'mention',
	HASHTAG = 'hashtag',
}

export interface Audio {
	type: string
	subtype?: string
	image?: Picture
	coverUrl?: string
	originCoverUrl?: string
	title: string
	author: string
}
